import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Smile, X } from "lucide-react";

/**
 * Emote wheel — 6 tap targets arranged in a circle. Selecting one broadcasts
 * a short-lived floating emote badge above the caller's chibi via /hub/emote.
 *
 * The wheel opens on top of the Hub UI and closes on selection or X.
 */
const EMOTES = [
  { key: "wave",  label: "Wave",   symbol: "👋" },
  { key: "bow",   label: "Bow",    symbol: "🙇" },
  { key: "cheer", label: "Cheers", symbol: "🍻" },
  { key: "laugh", label: "Laugh",  symbol: "😂" },
  { key: "sigh",  label: "Sigh",   symbol: "😮‍💨" },
  { key: "pray",  label: "Pray",   symbol: "🙏" },
];

export default function EmoteWheel() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const send = async (key) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.hubEmote(key);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Emote failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="emote-wheel-open"
        onClick={() => setOpen(true)}
        className="p-2 border border-[#333] hover:border-[#E5C34A] text-[#E5C34A] transition-colors"
        title="Emote"
      >
        <Smile className="w-4 h-4" strokeWidth={1.5} />
      </button>

      {open && (
        <div
          data-testid="emote-wheel"
          className="fixed inset-0 z-40 flex items-center justify-center"
          onClick={() => setOpen(false)}
          style={{ background: "rgba(5,5,5,0.55)", backdropFilter: "blur(2px)" }}
        >
          <div
            className="relative"
            style={{ width: 260, height: 260 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Ring bg */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                borderRadius: "50%",
                border: "1px solid #333",
                background:
                  "radial-gradient(60% 60% at 50% 50%, rgba(229,195,74,0.06), rgba(5,5,5,0.9))",
                boxShadow: "0 0 30px rgba(229,195,74,0.15), inset 0 0 20px rgba(0,0,0,0.7)",
              }}
            />

            {/* 6 buttons around the circle */}
            {EMOTES.map((e, i) => {
              const angle = (i / EMOTES.length) * Math.PI * 2 - Math.PI / 2;
              const r = 100;
              const x = Math.cos(angle) * r + 130 - 32;
              const y = Math.sin(angle) * r + 130 - 32;
              return (
                <button
                  key={e.key}
                  data-testid={`emote-${e.key}`}
                  disabled={busy}
                  onClick={() => send(e.key)}
                  className="absolute w-16 h-16 flex flex-col items-center justify-center border border-[#333] hover:border-[#E5C34A] bg-[#080606] transition-all disabled:opacity-40"
                  style={{
                    left: x, top: y,
                    animation: `emote-pop 0.35s ${(i * 0.04).toFixed(2)}s ease-out both`,
                    opacity: 0,
                    borderRadius: "50%",
                  }}
                >
                  <span className="text-2xl" aria-hidden>{e.symbol}</span>
                  <span className="font-mono text-[8px] tracking-widest uppercase text-[#8c8c8c] mt-0.5">
                    {e.label}
                  </span>
                </button>
              );
            })}

            {/* Close */}
            <button
              data-testid="emote-wheel-close"
              onClick={() => setOpen(false)}
              className="absolute w-10 h-10 flex items-center justify-center border border-[#333] hover:border-[#8A0303] bg-[#080606]"
              style={{
                left: 130 - 20,
                top: 130 - 20,
                borderRadius: "50%",
              }}
            >
              <X className="w-4 h-4 text-[#8A0303]" strokeWidth={1.5} />
            </button>
          </div>

          <style>{`
            @keyframes emote-pop {
              from { opacity: 0; transform: scale(0.4); }
              to   { opacity: 1; transform: scale(1); }
            }
          `}</style>
        </div>
      )}
    </>
  );
}
